import React, { useEffect, useMemo, useState, useRef } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import isEqual from "lodash/isEqual";

const createTaskFields = (title = "") => ({
  title,
  isFlatRate: true,
  flatRate: "",
  includedHours: "",
  hourlyRate: "",
  actualAmHours: "",
  chargeManual: false,
  manualHours: "",
  chargeOverageHours: false,
});

const isTaskSectionId = (sectionId) => String(sectionId || "").startsWith("task_");

const toApiBoolean = (value, fallback = false) => {
  if (value === undefined || value === null || value === "") return fallback;
  return value === true || value === 1 || value === "1" || value === "true";
};

const DecimalHoursInput = ({
  value,
  onChange,
  isInvalid,
  className = "input-field",
  disabled = false,
}) => (
  <NumericFormat
    value={value ?? ""}
    onValueChange={(values) => onChange?.({ target: { value: values.value } })}
    decimalScale={4}
    allowNegative={false}
    placeholder="0"
    customInput={Form.Control}
    isInvalid={isInvalid}
    className={className}
    disabled={disabled}
  />
);

const createRepRateFields = () => ({
  weekdayRateTitle: "Rep Hours Weekdays",
  weekdayNumberOfDays: "",
  weekdayNumberOfFTEs: "",
  weekdayHoursPerDay: "",
  weekdayIncludeInInvoice: true,
  weekdayChargeManual: false,
  weekdayManualHours: "",
  weekdayRate: "",
  weekendRateTitle: "Rep Hours Weekends",
  weekendNumberOfDays: "",
  weekendNumberOfFTEs: "",
  weekendHoursPerDay: "",
  weekendIncludeInInvoice: true,
  weekendChargeManual: false,
  weekendManualHours: "",
  weekendRate: "",
  socialMediaWeekdayRateTitle: "Social Media Weekdays",
  socialMediaWeekdayNumberOfDays: "",
  socialMediaWeekdayNumberOfFTEs: "",
  socialMediaWeekdayHoursPerDay: "",
  socialMediaWeekdayIncludeInInvoice: true,
  socialMediaWeekdayChargeManual: false,
  socialMediaWeekdayManualHours: "",
  socialMediaWeekdayRate: "",
  socialMediaWeekendRateTitle: "Social Media Weekends",
  socialMediaWeekendNumberOfDays: "",
  socialMediaWeekendNumberOfFTEs: "",
  socialMediaWeekendHoursPerDay: "",
  socialMediaWeekendIncludeInInvoice: true,
  socialMediaWeekendChargeManual: false,
  socialMediaWeekendManualHours: "",
  socialMediaWeekendRate: "",
  overtimeHourRateTitle: "Overtime Charge",
  overtimeIncludeInInvoice: true,
  overtimeHours: "",
  overtimeHourRate: "",
  holidayHourRateTitle: "Holiday Charge",
  holidayIncludeInInvoice: true,
  holidayHours: "",
  holidayHourRate: "",
});

const CurrencyInput = ({
  value,
  onChange,
  isInvalid,
  className = "input-field",
  disabled = false,
  style,
}) => (
  <NumericFormat
    value={value ?? ""}
    onValueChange={(values) => onChange?.({ target: { value: values.value } })}
    thousandSeparator
    decimalScale={2}
    allowNegative={false}
    prefix="$ "
    placeholder="$ 0.00"
    customInput={Form.Control}
    isInvalid={isInvalid}
    className={className}
    disabled={disabled}
    style={style}
  />
);

const toSectionLabel = (sectionId) => {
  const raw = String(sectionId || "").trim();
  const withoutTaskPrefix = raw.replace(/^task_/, "");
  return withoutTaskPrefix
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const resolveSectionOptionLabel = (sectionId, initialSectionData = {}) => {
  return (
    initialSectionData?.section_labels?.[sectionId] ||
    initialSectionData?.sections?.[sectionId]?.title ||
    initialSectionData?.[sectionId]?.title ||
    toSectionLabel(sectionId)
  );
};

const getVisibleSectionOptions = (
  selectedSections = [],
  sectionOptions = [],
  initialSectionData = {}
) => {
  if (Array.isArray(sectionOptions) && sectionOptions.length > 0) {
    const selectedIds = Array.isArray(selectedSections) ? selectedSections : [];

    const baseOptions = selectedIds.length
      ? sectionOptions.filter((option) => selectedIds.includes(option.id))
      : sectionOptions;

    const existingIds = new Set(baseOptions.map((option) => option.id));
    const missingSelectedOptions = selectedIds
      .filter((id) => !existingIds.has(id))
      .map((id) => ({
        id,
        label: resolveSectionOptionLabel(id, initialSectionData),
      }));

    if (missingSelectedOptions.length > 0) {
      return [...baseOptions, ...missingSelectedOptions];
    }

    return baseOptions;
  }

  return (selectedSections || []).map((sectionId) => ({
    id: sectionId,
    label: resolveSectionOptionLabel(sectionId, initialSectionData),
  }));
};

const LaborTab = ({
  selectedSections = [],
  sectionOptions = [],
  onSectionDataChange,
  initialSectionData = {},
  getFieldError = () => "",
}) => {
  const visibleSectionOptions = useMemo(
    () => getVisibleSectionOptions(selectedSections, sectionOptions, initialSectionData),
    [initialSectionData, sectionOptions, selectedSections]
  );

  const [sectionValues, setSectionValues] = useState({});

  const oldInitialRef = useRef(null);
  const oldVisibleOptionsRef = useRef(null);
  const isHydratingRef = useRef(false);
  const lastEmittedPayloadRef = useRef(null);

  useEffect(() => {
    const initialDataUnchanged = isEqual(oldInitialRef.current, initialSectionData);
    const visibleOptionsUnchanged = isEqual(
      oldVisibleOptionsRef.current,
      visibleSectionOptions
    );

    if (initialDataUnchanged && visibleOptionsUnchanged) {
      return;
    }

    // Only run if initial data or options actually changed
    const needsUpdate = oldInitialRef.current !== initialSectionData;
    const oldInitialSectionData = oldInitialRef.current;

    isHydratingRef.current = true;

    setSectionValues((prev) => {
      const next = {};

      visibleSectionOptions.forEach((section) => {
        const existing = initialSectionData?.[section.id] || {};
        const oldExisting = oldInitialSectionData?.[section.id] || {};
        const prevValue = prev?.[section.id];
        
        const isSectionDataChanged = JSON.stringify(oldExisting) !== JSON.stringify(existing);

        if (section.id === "rep_hours_onshore" || section.id === "rep_hours_offshore") {
          const base = createRepRateFields();
          
          if (!prevValue) {
            next[section.id] = { ...base, ...existing };
          } else {
            next[section.id] = { ...base, ...prevValue };
            // Deep merge only fields that changed
            if (isSectionDataChanged || needsUpdate) {
              Object.keys(existing).forEach((k) => {
                if (existing[k] !== oldExisting[k]) {
                  next[section.id][k] = existing[k];
                }
              });
            }
          }
          return;
        }

        const base = createTaskFields(section.label);
        const hasExplicitTitle = Object.prototype.hasOwnProperty.call(existing || {}, "title");

        if (!prevValue) {
          const normalizedIsFlatRate =
            existing?.isFlatRate === undefined
              ? true
              : toApiBoolean(existing?.isFlatRate, true);

          next[section.id] = {
            ...base,
            ...existing,
            title: hasExplicitTitle ? existing?.title : section.label || "",
            isFlatRate: normalizedIsFlatRate,
            chargeManual: toApiBoolean(existing?.chargeManual, false),
            chargeOverageHours: toApiBoolean(existing?.chargeOverageHours, false),
          };
        } else {
          next[section.id] = { ...base, ...prevValue };
          if (isSectionDataChanged || needsUpdate) {
            Object.keys(existing).forEach((k) => {
              if (existing[k] !== oldExisting[k]) {
                if (k === "chargeManual" || k === "chargeOverageHours") {
                  next[section.id][k] = toApiBoolean(existing[k], false);
                } else if (k === "isFlatRate") {
                  next[section.id][k] =
                    existing[k] === undefined
                      ? true
                      : toApiBoolean(existing[k], true);
                } else {
                  next[section.id][k] = existing[k];
                }
              }
            });
          }
          const hasTitle = Object.prototype.hasOwnProperty.call(next[section.id], "title");
          if (!hasTitle || !next[section.id].title) {
            next[section.id].title = section.label || "";
          }
          if (next[section.id]?.isFlatRate === undefined) {
            next[section.id].isFlatRate = true;
          } else {
            next[section.id].isFlatRate = toApiBoolean(next[section.id].isFlatRate, true);
          }
          if (next[section.id]?.chargeManual === undefined) {
            next[section.id].chargeManual = false;
          } else {
            next[section.id].chargeManual = toApiBoolean(
              next[section.id].chargeManual,
              false
            );
          }
        }
      });

      if (isEqual(next, prev)) {
        return prev;
      }
      return next;
    });

    oldInitialRef.current = initialSectionData;
    oldVisibleOptionsRef.current = visibleSectionOptions;
  }, [initialSectionData, visibleSectionOptions]);

  const onSectionDataChangeRef = useRef(onSectionDataChange);
  useEffect(() => {
    onSectionDataChangeRef.current = onSectionDataChange;
  }, [onSectionDataChange]);

  useEffect(() => {
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }

    if (isEqual(lastEmittedPayloadRef.current, sectionValues)) {
      return;
    }

    lastEmittedPayloadRef.current = sectionValues;
    onSectionDataChangeRef.current?.(sectionValues);
  }, [sectionValues]);

  const updateSectionValues = (updater) => {
    setSectionValues((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;

      if (isEqual(next, prev)) {
        return prev;
      }

      if (!isHydratingRef.current && !isEqual(lastEmittedPayloadRef.current, next)) {
        lastEmittedPayloadRef.current = next;
        onSectionDataChangeRef.current?.(next);
      }

      return next;
    });
  };

  const hasSelectedSection = visibleSectionOptions.length > 0;

  const orderedVisibleSectionOptions = useMemo(() => {
    const repSectionIds = new Set(["rep_hours_onshore", "rep_hours_offshore"]);
    const nonRepSections = visibleSectionOptions.filter(
      (section) => !repSectionIds.has(section.id)
    );
    const repSections = visibleSectionOptions.filter((section) =>
      repSectionIds.has(section.id)
    );
    return [...nonRepSections, ...repSections];
  }, [visibleSectionOptions]);

  const handleFieldChange = (sectionId, field, value) => {
    updateSectionValues((prev) => ({
      ...prev,
      [sectionId]: {
        ...(prev?.[sectionId] || createTaskFields()),
        [field]: value,
      },
    }));
  };

  const getError = (sectionId, field) => getFieldError?.(sectionId, field) || "";

  const renderRepRateSection = (sectionId, title, values) => {
    const tableRows = [
      {
        key: "weekday",
        titleField: "weekdayRateTitle",
        daysField: "weekdayNumberOfDays",
        ftesField: "weekdayNumberOfFTEs",
        hoursPerDayField: "weekdayHoursPerDay",
        includeInInvoiceField: "weekdayIncludeInInvoice",
        chargeManualField: "weekdayChargeManual",
        manualHoursField: "weekdayManualHours",
        hourlyRateField: "weekdayRate",
      },
      {
        key: "weekend",
        titleField: "weekendRateTitle",
        daysField: "weekendNumberOfDays",
        ftesField: "weekendNumberOfFTEs",
        hoursPerDayField: "weekendHoursPerDay",
        includeInInvoiceField: "weekendIncludeInInvoice",
        chargeManualField: "weekendChargeManual",
        manualHoursField: "weekendManualHours",
        hourlyRateField: "weekendRate",
      },
      {
        key: "socialMediaWeekday",
        titleField: "socialMediaWeekdayRateTitle",
        daysField: "socialMediaWeekdayNumberOfDays",
        ftesField: "socialMediaWeekdayNumberOfFTEs",
        hoursPerDayField: "socialMediaWeekdayHoursPerDay",
        includeInInvoiceField: "socialMediaWeekdayIncludeInInvoice",
        chargeManualField: "socialMediaWeekdayChargeManual",
        manualHoursField: "socialMediaWeekdayManualHours",
        hourlyRateField: "socialMediaWeekdayRate",
      },
      {
        key: "socialMediaWeekend",
        titleField: "socialMediaWeekendRateTitle",
        daysField: "socialMediaWeekendNumberOfDays",
        ftesField: "socialMediaWeekendNumberOfFTEs",
        hoursPerDayField: "socialMediaWeekendHoursPerDay",
        includeInInvoiceField: "socialMediaWeekendIncludeInInvoice",
        chargeManualField: "socialMediaWeekendChargeManual",
        manualHoursField: "socialMediaWeekendManualHours",
        hourlyRateField: "socialMediaWeekendRate",
      },
    ];

    const computeTotal = (days, ftes, hoursPerDay) => {
      const d = parseFloat(days) || 0;
      const f = parseFloat(ftes) || 0;
      const h = parseFloat(hoursPerDay) || 0;
      const total = d * f * h;
      return total > 0 ? Number(total.toFixed(2)).toString() : "-";
    };

    return (
      <>
        <div className="rep-rate-table">
          <div className="rep-rate-table-header" style={{ display: 'flex', gap: '10px' }}>
            <div className="rep-rate-col" style={{ flex: '1' }}>Add to Invoice</div>
            <div className="rep-rate-col rep-rate-col-category" style={{ flex: '2' }}>Work Category</div>
            <div className="rep-rate-col rep-rate-col-days" style={{ flex: '1' }}># of Days</div>
            <div className="rep-rate-col" style={{ flex: '1' }}># of FTEs</div>
            <div className="rep-rate-col rep-rate-col-hours" style={{ flex: '1' }}># Hours/Day</div>
            <div className="rep-rate-col" style={{ flex: '1' }}>Charge Manual</div>
            <div className="rep-rate-col" style={{ flex: '1' }}>Manual Hours</div>
            <div className="rep-rate-col rep-rate-col-total" style={{ flex: '1' }}>Total Hours</div>
            <div className="rep-rate-col rep-rate-col-rate" style={{ flex: '1' }}>Hourly Rate ($)</div>
          </div>

          {tableRows.map((row) => {
            const days = values[row.daysField];
            const ftes = values[row.ftesField];
            const hoursPerDay = values[row.hoursPerDayField];
            const includeInInvoice =
              values[row.includeInInvoiceField] === undefined
                ? true
                : Boolean(values[row.includeInInvoiceField]);
            const chargeManual = toApiBoolean(values[row.chargeManualField], false);
            const manualHours = values[row.manualHoursField];
            const hourlyRate = values[row.hourlyRateField];
            const total = computeTotal(days, ftes, hoursPerDay);

            return (
              <div className="rep-rate-table-row mt-2" style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }} key={row.key}>
                <div className="rep-rate-col" style={{ flex: '1' }}>
                  <Form.Check
                    type="switch"
                    label=""
                    checked={includeInInvoice}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      updateSectionValues((prev) => {
                        const currentSection = prev?.[sectionId] || createRepRateFields();
                        return {
                          ...prev,
                          [sectionId]: {
                            ...currentSection,
                            [row.includeInInvoiceField]: checked,
                            ...(checked
                              ? {}
                              : {
                                  [row.manualHoursField]: "",
                                }),
                          },
                        };
                      });
                    }}
                    className="mt-2"
                  />
                </div>

                <div className="rep-rate-col rep-rate-col-category" style={{ flex: '2' }}>
                  <div className="rep-rate-category-cell">
                    <Form.Control
                      type="text"
                      value={values[row.titleField] ?? ""}
                      onChange={(e) =>
                        handleFieldChange(sectionId, row.titleField, e.target.value)
                      }
                      isInvalid={Boolean(getError(sectionId, row.titleField))}
                      disabled={!includeInInvoice}
                      className="input-field rep-rate-title-input"
                      style={{ backgroundColor: includeInInvoice ? '#fff' : '#e9ecef' }}
                    />
                    {days !== "" && days !== undefined && (
                      <span className="rep-rate-days-badge ms-2 badge bg-secondary">{days}</span>
                    )}
                  </div>
                  <Form.Control.Feedback type="invalid">
                    {getError(sectionId, row.titleField)}
                  </Form.Control.Feedback>
                </div>

                <div className="rep-rate-col rep-rate-col-days" style={{ flex: '1' }}>
                  <Form.Control
                    type="number"
                    min="0"
                    value={days ?? ""}
                    onChange={(e) =>
                      handleFieldChange(sectionId, row.daysField, e.target.value)
                    }
                    isInvalid={Boolean(getError(sectionId, row.daysField))}
                    disabled={!includeInInvoice || chargeManual}
                    className="input-field"
                    style={{ backgroundColor: includeInInvoice ? '#fff' : '#e9ecef' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError(sectionId, row.daysField)}
                  </Form.Control.Feedback>
                </div>

                <div className="rep-rate-col rep-rate-col-ftes" style={{ flex: '1' }}>
                  <Form.Control
                    type="number"
                    min="0"
                    value={ftes ?? ""}
                    onChange={(e) =>
                      handleFieldChange(sectionId, row.ftesField, e.target.value)
                    }
                    isInvalid={Boolean(getError(sectionId, row.ftesField))}
                    disabled={!includeInInvoice || chargeManual}
                    className="input-field"
                    style={{ backgroundColor: includeInInvoice ? '#fff' : '#e9ecef' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError(sectionId, row.ftesField)}
                  </Form.Control.Feedback>
                </div>

                <div className="rep-rate-col rep-rate-col-hours" style={{ flex: '1' }}>
                  <Form.Control
                    type="number"
                    min="0"
                    value={hoursPerDay ?? ""}
                    onChange={(e) =>
                      handleFieldChange(sectionId, row.hoursPerDayField, e.target.value)
                    }
                    isInvalid={Boolean(getError(sectionId, row.hoursPerDayField))}
                    disabled={!includeInInvoice || chargeManual}
                    className="input-field"
                    style={{ backgroundColor: includeInInvoice ? '#fff' : '#e9ecef' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError(sectionId, row.hoursPerDayField)}
                  </Form.Control.Feedback>
                </div>

                <div className="rep-rate-col" style={{ flex: '1' }}>
                  <Form.Check
                    type="switch"
                    label=""
                    checked={chargeManual}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      updateSectionValues((prev) => {
                        const currentSection = prev?.[sectionId] || createRepRateFields();
                        return {
                          ...prev,
                          [sectionId]: {
                            ...currentSection,
                            [row.chargeManualField]: checked,
                            ...(checked
                              ? {}
                              : {
                                  [row.manualHoursField]: "",
                                }),
                          },
                        };
                      });
                    }}
                    disabled={!includeInInvoice}
                    className="mt-2"
                  />
                </div>

                <div className="rep-rate-col" style={{ flex: '1' }}>
                  <Form.Control
                    type="number"
                    min="0"
                    value={manualHours ?? ""}
                    onChange={(e) =>
                      handleFieldChange(sectionId, row.manualHoursField, e.target.value)
                    }
                    isInvalid={Boolean(getError(sectionId, row.manualHoursField))}
                    disabled={!includeInInvoice || !chargeManual}
                    className="input-field"
                    style={{ backgroundColor: includeInInvoice ? '#fff' : '#e9ecef' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError(sectionId, row.manualHoursField)}
                  </Form.Control.Feedback>
                </div>

                <div className="rep-rate-col rep-rate-col-total" style={{ flex: '1' }}>
                  <div className="rep-rate-total-display p-2 rounded text-center border">{total}</div>
                </div>

                <div className="rep-rate-col rep-rate-col-rate" style={{ flex: '1' }}>
                  <CurrencyInput
                    value={hourlyRate}
                    onChange={(e) =>
                      handleFieldChange(sectionId, row.hourlyRateField, e.target.value)
                    }
                    isInvalid={Boolean(getError(sectionId, row.hourlyRateField))}
                    disabled={!includeInInvoice}
                    style={{ backgroundColor: includeInInvoice ? '#fff' : '#e9ecef' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError(sectionId, row.hourlyRateField)}
                  </Form.Control.Feedback>
                </div>
              </div>
            );
          })}
        </div>

        {(() => {
          const overtimeIncludeInInvoice =
            values.overtimeIncludeInInvoice === undefined
              ? true
              : Boolean(values.overtimeIncludeInInvoice);

          return (
        <Row className="mt-3">
          <Col lg={3}>
            <Form.Group className="mb-3">
              <Form.Label className="label-input">Overtime Charge Title</Form.Label>
              <Form.Control
                type="text"
                value={values.overtimeHourRateTitle}
                onChange={(event) =>
                  handleFieldChange(sectionId, "overtimeHourRateTitle", event.target.value)
                }
                isInvalid={Boolean(getError(sectionId, "overtimeHourRateTitle"))}
                disabled={!overtimeIncludeInInvoice}
                className="input-field"
              />
              <Form.Control.Feedback type="invalid">
                {getError(sectionId, "overtimeHourRateTitle")}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col lg={3}>
            <Form.Group className="mb-3">
              <Form.Label className="label-input">Overtime Hours</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={values.overtimeHours ?? ""}
                onChange={(event) =>
                  handleFieldChange(sectionId, "overtimeHours", event.target.value)
                }
                isInvalid={Boolean(getError(sectionId, "overtimeHours"))}
                disabled={!overtimeIncludeInInvoice}
                className="input-field"
              />
              <Form.Control.Feedback type="invalid">
                {getError(sectionId, "overtimeHours")}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col lg={3}>
            <Form.Group className="mb-3">
              <Form.Label className="label-input">Overtime Hour Rate ($)</Form.Label>
              <CurrencyInput
                value={values.overtimeHourRate}
                onChange={(event) =>
                  handleFieldChange(sectionId, "overtimeHourRate", event.target.value)
                }
                isInvalid={Boolean(getError(sectionId, "overtimeHourRate"))}
                disabled={!overtimeIncludeInInvoice}
              />
              <Form.Control.Feedback type="invalid">
                {getError(sectionId, "overtimeHourRate")}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col lg={3}>
            <Form.Group className="mb-3">
              <Form.Label className="label-input">Add to Invoice</Form.Label>
              <Form.Check
                type="switch"
                checked={overtimeIncludeInInvoice}
                onChange={(event) =>
                  handleFieldChange(sectionId, "overtimeIncludeInInvoice", event.target.checked)
                }
                className="mt-2"
              />
            </Form.Group>
          </Col>
        </Row>
          );
        })()}

        {(() => {
          const holidayIncludeInInvoice =
            values.holidayIncludeInInvoice === undefined
              ? true
              : Boolean(values.holidayIncludeInInvoice);

          return (
        <Row>
          <Col lg={3}>
            <Form.Group className="mb-3">
              <Form.Label className="label-input">Holiday Charge Title</Form.Label>
              <Form.Control
                type="text"
                value={values.holidayHourRateTitle}
                onChange={(event) =>
                  handleFieldChange(sectionId, "holidayHourRateTitle", event.target.value)
                }
                isInvalid={Boolean(getError(sectionId, "holidayHourRateTitle"))}
                disabled={!holidayIncludeInInvoice}
                className="input-field"
              />
              <Form.Control.Feedback type="invalid">
                {getError(sectionId, "holidayHourRateTitle")}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col lg={3}>
            <Form.Group className="mb-3">
              <Form.Label className="label-input">Holiday Hours</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={values.holidayHours ?? ""}
                onChange={(event) =>
                  handleFieldChange(sectionId, "holidayHours", event.target.value)
                }
                isInvalid={Boolean(getError(sectionId, "holidayHours"))}
                disabled={!holidayIncludeInInvoice}
                className="input-field"
              />
              <Form.Control.Feedback type="invalid">
                {getError(sectionId, "holidayHours")}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col lg={3}>
            <Form.Group className="mb-3">
              <Form.Label className="label-input">Holiday Hour Rate ($)</Form.Label>
              <CurrencyInput
                value={values.holidayHourRate}
                onChange={(event) =>
                  handleFieldChange(sectionId, "holidayHourRate", event.target.value)
                }
                isInvalid={Boolean(getError(sectionId, "holidayHourRate"))}
                disabled={!holidayIncludeInInvoice}
              />
              <Form.Control.Feedback type="invalid">
                {getError(sectionId, "holidayHourRate")}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col lg={3}>
            <Form.Group className="mb-3">
              <Form.Label className="label-input">Add to Invoice</Form.Label>
              <Form.Check
                type="switch"
                checked={holidayIncludeInInvoice}
                onChange={(event) =>
                  handleFieldChange(sectionId, "holidayIncludeInInvoice", event.target.checked)
                }
                className="mt-2"
              />
            </Form.Group>
          </Col>
        </Row>
          );
        })()}
      </>
    );
  };

  return (
    <div className="labor-tab-section">
      {!hasSelectedSection && (
        <div className="invoice-section-div mb-0 text-center">
          <span className="font-16 font-weight-bold heading-color">
            No labor tasks found for this program.
          </span>
        </div>
      )}

      {orderedVisibleSectionOptions.map((section, index) => {
        const isRepSection =
          section.id === "rep_hours_onshore" || section.id === "rep_hours_offshore";
        const values = isRepSection
          ? sectionValues?.[section.id] || createRepRateFields()
          : sectionValues?.[section.id] || createTaskFields(section.label);
        const isTaskSection = isTaskSectionId(section.id);
        const isFlatRate = toApiBoolean(values.isFlatRate, true);
        const isHourlyMode = !isFlatRate;
        const showTaskHourOverride = isTaskSection && isHourlyMode;
        const chargeManual = toApiBoolean(values.chargeManual, false);

        return (
          <div
            className={`invoice-section-div ${index === orderedVisibleSectionOptions.length - 1 ? "mb-0" : ""}`}
            key={section.id}
          >
            <h3 className="d-flex align-items-center gap-2">
              <img src="/assets/images/invoice-user-icon.svg" alt="" />
              <span className="font-16 font-weight-bold heading-color">
                {section.label}
              </span>
            </h3>

            {isRepSection ? (
              renderRepRateSection(section.id, section.label, values)
            ) : (
              <>
                <Row>
                  <Col lg={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Charge flat rate (uncheck for hourly rate)"
                        checked={isFlatRate}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          updateSectionValues((prev) => {
                            const currentSection =
                              prev?.[section.id] || createTaskFields(section.label);
                            return {
                              ...prev,
                              [section.id]: {
                                ...currentSection,
                                isFlatRate: checked,
                                ...(checked
                                  ? {
                                      chargeManual: false,
                                      manualHours: "",
                                    }
                                  : {}),
                              },
                            };
                          });
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">Title</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.title}
                        onChange={(event) =>
                          handleFieldChange(section.id, "title", event.target.value)
                        }
                        isInvalid={Boolean(getError(section.id, "title"))}
                        className="input-field"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getError(section.id, "title")}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  {isFlatRate ? (
                    <Col lg={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="label-input">Flat Rate ($)</Form.Label>
                        <CurrencyInput
                          value={values.flatRate}
                          onChange={(event) =>
                            handleFieldChange(section.id, "flatRate", event.target.value)
                          }
                          isInvalid={Boolean(getError(section.id, "flatRate"))}
                        />
                        <Form.Control.Feedback type="invalid">
                          {getError(section.id, "flatRate")}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  ) : null}
                </Row>

                <Row>
                  {isFlatRate ? (
                    <Col lg={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="label-input">Included Hours</Form.Label>
                        <Form.Control
                          type="text"
                          value={values.includedHours}
                          onChange={(event) =>
                            handleFieldChange(section.id, "includedHours", event.target.value)
                          }
                          isInvalid={Boolean(getError(section.id, "includedHours"))}
                          className="input-field"
                        />
                        <Form.Control.Feedback type="invalid">
                          {getError(section.id, "includedHours")}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  ) : null}
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">Hourly Rate</Form.Label>
                      <CurrencyInput
                        value={values.hourlyRate}
                        onChange={(event) =>
                          handleFieldChange(section.id, "hourlyRate", event.target.value)
                        }
                        isInvalid={Boolean(getError(section.id, "hourlyRate"))}
                      />
                      <Form.Control.Feedback type="invalid">
                        {getError(section.id, "hourlyRate")}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">Actual AM Hours</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.actualAmHours ?? ""}
                        readOnly
                        title="Time logged for this task for the invoice month"
                        className="input-field"
                      />
                    </Form.Group>
                  </Col>
                  {isFlatRate ? (
                    <Col lg={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="label-input">Overage Hours</Form.Label>
                        <Form.Control
                          type="text"
                          value={values.actualAmHours ? Math.max(0, (parseFloat(values.actualAmHours || 0) - parseFloat(values.includedHours || 0))).toFixed(2) : ""}
                          readOnly
                          title="Calculated: actualAmHours - includedHours"
                          className="input-field"
                        />
                      </Form.Group>
                    </Col>
                  ) : null}
                </Row>

                {showTaskHourOverride ? (
                  <Row>
                    <Col lg={6}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          label="Override actual hours"
                          checked={chargeManual}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            updateSectionValues((prev) => {
                              const currentSection =
                                prev?.[section.id] || createTaskFields(section.label);
                              return {
                                ...prev,
                                [section.id]: {
                                  ...currentSection,
                                  chargeManual: checked,
                                  ...(checked ? {} : { manualHours: "" }),
                                },
                              };
                            });
                          }}
                        />
                      </Form.Group>
                    </Col>
                    {chargeManual ? (
                      <Col lg={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="label-input">Manual Hours</Form.Label>
                          <DecimalHoursInput
                            value={values.manualHours ?? ""}
                            onChange={(event) =>
                              handleFieldChange(section.id, "manualHours", event.target.value)
                            }
                            isInvalid={Boolean(getError(section.id, "manualHours"))}
                          />
                          <Form.Control.Feedback type="invalid">
                            {getError(section.id, "manualHours")}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted">
                            Invoice billing will use manual hours instead of actual AM hours.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    ) : null}
                  </Row>
                ) : null}

                {isFlatRate ? (
                  <Form.Group className="mb-0">
                    <Form.Check
                      type="switch"
                      label="Charge overage hours"
                      checked={toApiBoolean(values.chargeOverageHours, false)}
                      onChange={(event) =>
                        handleFieldChange(
                          section.id,
                          "chargeOverageHours",
                          event.target.checked
                        )
                      }
                    />
                  </Form.Group>
                ) : null}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LaborTab;
